import { HttpBackend, HttpClient, HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, Observable, shareReplay, switchMap, throwError } from 'rxjs';
import { TokenService } from '../services/token.service';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';
import type { AuthResponse } from '../services/auth.service';
import { SessionExpiredService } from '../services/session-expired.service';

const PUBLIC_AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/recuperar-password',
  '/auth/restablecer-password',
  '/gestoria/ajustes/email-remitente/verificar',
] as const;

const TOKEN_REFRESH_BUFFER_MS = 60_000;

let refreshRequest$: Observable<AuthResponse> | null = null;

export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const router       = inject(Router);
  const authService  = inject(AuthService);
  const sessionExpired = inject(SessionExpiredService);
  const httpBackend  = inject(HttpBackend);
  const token        = tokenService.get();
  const isApiUrl     = req.url.startsWith(environment.apiUrl);
  const hadToken     = !!token;

  if (
    token &&
    isRefreshableRequest(req.url, isApiUrl) &&
    tokenService.isExpiredOrExpiringSoon(TOKEN_REFRESH_BUFFER_MS)
  ) {
    return refreshToken(httpBackend, token).pipe(
      switchMap((response) => {
        const freshToken = persistAuthResponse(response, tokenService, authService);
        return next(withAuthorization(req, freshToken));
      }),
      catchError((refreshErr: HttpErrorResponse) =>
        handleRefreshFailure(refreshErr, authService, sessionExpired, router),
      ),
    );
  }

  const authorizedReq = token && isApiUrl && !isPublicAuthEndpoint(req.url)
    ? withAuthorization(req, token)
    : req;

  return next(authorizedReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401 && err.error?.code === 'simulacion_expirada') {
        authService.clearLocalSession();
        void router.navigate(['/login'], {
          queryParams: { reason: 'simulation_expired' },
        });
      }

      if (isRefreshableAuthError(err, req.url, isApiUrl, hadToken)) {
        const currentToken = tokenService.get();

        if (currentToken && currentToken !== token) {
          return next(withAuthorization(req, currentToken));
        }

        return refreshToken(httpBackend, token!).pipe(
          switchMap((response) => {
            const freshToken = persistAuthResponse(response, tokenService, authService);
            return next(withAuthorization(req, freshToken));
          }),
          catchError((refreshErr: HttpErrorResponse) =>
            handleRefreshFailure(refreshErr, authService, sessionExpired, router),
          ),
        );
      }

      return throwError(() => err);
    }),
  );
};

function isRefreshableAuthError(
  err: HttpErrorResponse,
  requestUrl: string,
  isApiUrl: boolean,
  hadToken: boolean,
): boolean {
  if (err.status !== 401 || !hadToken) return false;
  if (err.error?.code === 'simulacion_expirada') return false;
  return isRefreshableRequest(requestUrl, isApiUrl);
}

function refreshToken(httpBackend: HttpBackend, token: string): Observable<AuthResponse> {
  if (!refreshRequest$) {
    const http = new HttpClient(httpBackend);

    refreshRequest$ = http.post<AuthResponse>(
      `${environment.apiUrl}/auth/refresh`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    ).pipe(
      finalize(() => {
        refreshRequest$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  return refreshRequest$;
}

function persistAuthResponse(
  response: AuthResponse,
  tokenService: TokenService,
  authService: AuthService,
): string {
  const remember = tokenService.isRemembered();
  tokenService.set(response.access_token, remember, response.expires_in);
  authService.updateCurrentUser(response.user);

  return response.access_token;
}

function handleRefreshFailure(
  refreshErr: HttpErrorResponse,
  authService: AuthService,
  sessionExpired: SessionExpiredService,
  router: Router,
): Observable<never> {
  if (refreshErr.status === 401) {
    closeExpiredSession(authService, sessionExpired, router);
  }

  return throwError(() => refreshErr);
}

function withAuthorization<T>(req: HttpRequest<T>, token: string): HttpRequest<T> {
  return req.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
}

function closeExpiredSession(
  authService: AuthService,
  sessionExpired: SessionExpiredService,
  router: Router,
): void {
  authService.clearLocalSession();
  sessionExpired.open();
  void router.navigate(['/login'], {
    queryParams: { reason: 'session_expired' },
    replaceUrl: true,
  });
}

function isPublicAuthEndpoint(requestUrl: string): boolean {
  const apiBase = environment.apiUrl.replace(/\/$/, '');
  const normalizedUrl = requestUrl.split('?')[0].replace(/\/$/, '');

  return PUBLIC_AUTH_ENDPOINTS.some((path) => normalizedUrl === `${apiBase}${path}`)
    || normalizedUrl.startsWith(`${apiBase}/invitaciones/`);
}

function isRefreshEndpoint(requestUrl: string): boolean {
  const apiBase = environment.apiUrl.replace(/\/$/, '');
  const normalizedUrl = requestUrl.split('?')[0].replace(/\/$/, '');

  return normalizedUrl === `${apiBase}/auth/refresh`;
}

function isRefreshableRequest(requestUrl: string, isApiUrl: boolean): boolean {
  return isApiUrl && !isPublicAuthEndpoint(requestUrl) && !isRefreshEndpoint(requestUrl);
}
